import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { buildDate } from '../../environments/version';

/** How often the running app re-checks the server for a newer build. */
const POLL_INTERVAL_MS = 5 * 60 * 1000;

/** Query param used to force the browser to treat index.html as a new resource. */
const BUST_PARAM = '_b';

/** Remembers which build we already reloaded for, so a bad deploy can't loop us. */
const RELOAD_GUARD_KEY = 'pp_reloaded_for_build';

@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private pendingBuild: string | null = null;

  constructor(private router: Router) {}

  start(): void {
    this.stripBustParam();

    // On a fresh load there is no unsaved work, so a stale build can be swapped immediately.
    this.check(true);

    setInterval(() => this.check(false), POLL_INTERVAL_MS);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.check(false);
      }
    });

    // A newer build found mid-session waits for a navigation, which is already a point
    // where the user is discarding whatever was on screen. Avoids nuking a half-filled claim form.
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.pendingBuild) {
          this.reloadTo(this.pendingBuild);
        }
      });
  }

  private async check(reloadImmediately: boolean): Promise<void> {
    let serverBuild: string;
    try {
      // The unique query string gives the request its own cache key, and no-store keeps
      // the response out of the cache. This is what lets a cached app see past its own cache.
      const res = await fetch(`assets/build-info.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) {
        return;
      }
      serverBuild = (await res.json()).buildDate;
    } catch {
      return; // Offline or blocked: leave the running app alone.
    }

    if (!serverBuild || serverBuild === buildDate) {
      return;
    }

    if (reloadImmediately) {
      this.reloadTo(serverBuild);
    } else {
      this.pendingBuild = serverBuild;
    }
  }

  private reloadTo(serverBuild: string): void {
    if (sessionStorage.getItem(RELOAD_GUARD_KEY) === serverBuild) {
      return;
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, serverBuild);

    const url = new URL(window.location.href);
    url.searchParams.set(BUST_PARAM, serverBuild);
    window.location.replace(url.toString());
  }

  private stripBustParam(): void {
    const url = new URL(window.location.href);
    if (url.searchParams.has(BUST_PARAM)) {
      url.searchParams.delete(BUST_PARAM);
      window.history.replaceState({}, '', url.toString());
    }
  }
}
